import { z } from 'zod';
import { TenantRoleSchema } from './permissions';

export const TenantSlugSchema = z
  .string()
  .min(3)
  .max(40)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'lowercase letters, digits and hyphens only');

export const TENANT_PLANS = ['free', 'team', 'business'] as const;
export const TenantPlanSchema = z.enum(TENANT_PLANS);

const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'hex color such as #1d4ed8');

export const TenantBrandingSchema = z
  .object({
    /** Product name shown in the admin app and emails. */
    productName: z.string().min(1).max(60),
    /** Help-center title shown to requesters in the portal. */
    portalName: z.string().min(1).max(80),
    logoUrl: z.url().nullable(),
    primaryColor: HexColorSchema,
  })
  .partial();

export const TenantSettingsSchema = z
  .object({
    branding: TenantBrandingSchema,
    defaultTimezone: z.string().min(1).max(64),
  })
  .partial();
export type TenantSettings = z.infer<typeof TenantSettingsSchema>;

export const TenantSchema = z.object({
  id: z.uuid(),
  slug: TenantSlugSchema,
  name: z.string().min(1).max(120),
  plan: TenantPlanSchema,
  settings: TenantSettingsSchema,
  configVersion: z.int().min(1),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Tenant = z.infer<typeof TenantSchema>;

export const CreateTenantSchema = z.strictObject({
  slug: TenantSlugSchema,
  name: z.string().min(1).max(120),
  plan: TenantPlanSchema.default('free'),
  ownerEmail: z.email(),
  ownerDisplayName: z.string().min(1).max(120),
});
export type CreateTenant = z.infer<typeof CreateTenantSchema>;

export const USER_KINDS = ['member', 'guest', 'service', 'imported'] as const;
export const UserKindSchema = z.enum(USER_KINDS);

export const USER_STATUSES = ['active', 'invited', 'disabled'] as const;
export const UserStatusSchema = z.enum(USER_STATUSES);

export const AppUserSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  /** Null for imported people who have never logged in. */
  identityId: z.uuid().nullable(),
  email: z.email(),
  displayName: z.string().min(1).max(120),
  avatarUrl: z.url().nullable(),
  kind: UserKindSchema,
  status: UserStatusSchema,
  timezone: z.string().max(64).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type AppUser = z.infer<typeof AppUserSchema>;

export const InviteUserSchema = z.strictObject({
  email: z.email(),
  displayName: z.string().min(1).max(120),
  tenantRole: TenantRoleSchema.default('member'),
});
export type InviteUser = z.infer<typeof InviteUserSchema>;

export const MembershipSchema = z.object({
  tenantId: z.uuid(),
  userId: z.uuid(),
  tenantRole: TenantRoleSchema,
  invitedBy: z.uuid().nullable(),
  joinedAt: z.iso.datetime().nullable(),
});
export type Membership = z.infer<typeof MembershipSchema>;

/** Which app a token was issued for; decides which route group is reachable. */
export const SURFACES = ['admin', 'portal'] as const;
export const SurfaceSchema = z.enum(SURFACES);
export type Surface = z.infer<typeof SurfaceSchema>;

/**
 * Claims the API trusts after the gateway has verified the JWT. The Cognito
 * pre-token-generation trigger writes `tenant_id`, `user_id` and `tenant_role`.
 */
export const PrincipalClaimsSchema = z.object({
  sub: z.string().min(1),
  tenant_id: z.uuid(),
  user_id: z.uuid(),
  tenant_role: TenantRoleSchema,
  email: z.email().optional(),
  surface: SurfaceSchema.optional(),
});
export type PrincipalClaims = z.infer<typeof PrincipalClaimsSchema>;

/* ----------------------------- Bolt 3: admin DTOs ----------------------------- */

/** IANA time zone validated with the platform's own database (Node 18+ and browsers). */
export const isValidTimeZone = (value: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
};
export const TimeZoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine(isValidTimeZone, 'unknown IANA time zone');

export const UpdateTenantSchema = z
  .strictObject({
    name: z.string().min(1).max(120),
    settings: TenantSettingsSchema,
  })
  .partial();
export type UpdateTenant = z.infer<typeof UpdateTenantSchema>;

export const UpdateUserSchema = z
  .strictObject({
    displayName: z.string().min(1).max(120),
    avatarUrl: z.url().nullable(),
    timezone: TimeZoneSchema.nullable(),
  })
  .partial();
export type UpdateUser = z.infer<typeof UpdateUserSchema>;

export const SetMembershipSchema = z.strictObject({
  tenantRole: TenantRoleSchema,
});
export type SetMembership = z.infer<typeof SetMembershipSchema>;

export const GROUP_KINDS = ['group', 'team'] as const;
export const GroupKindSchema = z.enum(GROUP_KINDS);
export type GroupKind = z.infer<typeof GroupKindSchema>;

export const GroupSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  name: z.string().min(1).max(80),
  kind: GroupKindSchema,
  timeZone: z.string().nullable(),
  settings: z.record(z.string(), z.unknown()),
  memberCount: z.int().min(0).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Group = z.infer<typeof GroupSchema>;

export const CreateGroupSchema = z.strictObject({
  name: z.string().min(1).max(80),
  kind: GroupKindSchema.default('group'),
  timeZone: TimeZoneSchema.nullable().default(null),
  settings: z.record(z.string(), z.unknown()).default({}),
});
export type CreateGroup = z.infer<typeof CreateGroupSchema>;

export const UpdateGroupSchema = z
  .strictObject({
    name: z.string().min(1).max(80),
    kind: GroupKindSchema,
    timeZone: TimeZoneSchema.nullable(),
    settings: z.record(z.string(), z.unknown()),
  })
  .partial();
export type UpdateGroup = z.infer<typeof UpdateGroupSchema>;

export const OrganizationSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  name: z.string().min(1).max(120),
  memberCount: z.int().min(0).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Organization = z.infer<typeof OrganizationSchema>;

export const CreateOrganizationSchema = z.strictObject({
  name: z.string().min(1).max(120),
});
export type CreateOrganization = z.infer<typeof CreateOrganizationSchema>;

export const UpdateOrganizationSchema = CreateOrganizationSchema.partial();
export type UpdateOrganization = z.infer<typeof UpdateOrganizationSchema>;

export const AUDIT_ACTIONS = ['created', 'updated', 'deleted'] as const;
export const AuditActionSchema = z.enum(AUDIT_ACTIONS);

export const AUDIT_ENTITY_TYPES = [
  'tenant',
  'user',
  'membership',
  'group',
  'group_member',
  'organization',
  'organization_member',
  // Bolt 4: project configuration
  'project',
  'issue_type',
  'priority',
  'field',
  'field_option',
  'field_binding',
  'form',
  'workflow',
  'status',
  'transition',
  'view',
  'sla_policy',
  'project_template',
  'canned_response',
  'role',
  'role_assignment',
  'permission_scheme',
  'permission_grant',
  'job',
  // Bolt 5: platform API
  'api_key',
  'webhook',
  'link_type',
  // Bolt 8: automations, SLA, approvals, notifications
  'automation_rule',
  // Bolt 9: import framework
  'import_batch',
] as const;
export const AuditEntityTypeSchema = z.enum(AUDIT_ENTITY_TYPES);
export type AuditEntityType = z.infer<typeof AuditEntityTypeSchema>;

export const ConfigAuditEntrySchema = z.object({
  id: z.uuid(),
  actor: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
  entityType: AuditEntityTypeSchema,
  entityId: z.uuid(),
  action: AuditActionSchema,
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  occurredAt: z.iso.datetime(),
});
export type ConfigAuditEntry = z.infer<typeof ConfigAuditEntrySchema>;

export const AuditQuerySchema = z.object({
  entityType: AuditEntityTypeSchema.optional(),
  entityId: z.uuid().optional(),
  actorUserId: z.uuid().optional(),
  since: z.iso.datetime().optional(),
  until: z.iso.datetime().optional(),
});

/** `GET /admin/v1/me`: everything the shell needs to render the caller's context in one call. */
export const MeResponseSchema = z.object({
  user: AppUserSchema,
  tenant: TenantSchema.pick({ id: true, slug: true, name: true, plan: true, settings: true }),
  tenantRole: TenantRoleSchema,
  groups: z.array(GroupSchema.pick({ id: true, name: true, kind: true })),
  organizations: z.array(OrganizationSchema.pick({ id: true, name: true })),
  /**
   * Bolt 10: `REQUIRE_ADMIN_MFA` as deployed (`apps/auth-triggers` enforces it; this is just
   * the admin-web hint). Added additively — existing consumers of `MeResponse` are unaffected.
   */
  requireAdminMfa: z.boolean(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;
